import { Bell, Compass, LayoutGrid, LogOut, MessageSquare, Settings, Sparkles, User as UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';

import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui';
import {
  useAuth,
  useHasRole,
  useIsExternalClient,
  useIsInternalUser,
  useTier,
} from '@/lib/auth/AuthProvider';
import { cn } from '@/lib/cn';

import { LanguageSwitcher } from './LanguageSwitcher';

/**
 * Persistent app shell header (Spec S03 §"Header global").
 *
 * Composition is role-aware:
 *   - Primary tabs (Chat / Map / Dashboards) hidden for External Free tier
 *   - Quota indicator pill shown only for external_client
 *   - Studio switcher shown for analysts (internal dual-role)
 *   - Admin entry inside the user menu shown only for `administrator`
 */
export function GlobalHeader() {
  const { t } = useTranslation(['app', 'studio', 'auth', 'common']);
  const isExternalClient = useIsExternalClient();
  const isInternalUser = useIsInternalUser();
  const isAnalyst = useHasRole('data_analyst_standard', 'data_analyst_domain_expert');
  const isAdmin = useHasRole('administrator');
  const tier = useTier();

  const showPrimaryTabs = !(isExternalClient && tier === 'free');

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-4 border-b border-slate-200 bg-white px-4">
      <Link to="/app" className="flex items-center gap-2 font-semibold text-brand-900">
        <Sparkles className="h-5 w-5 text-accent" aria-hidden />
        <span>{t('auth:appName')}</span>
      </Link>

      {showPrimaryTabs && (
        <nav className="flex items-center gap-1" aria-label="Primary">
          <PrimaryTab to="/app/chat" icon={<MessageSquare className="h-4 w-4" />} label={t('app:nav.chat')} />
          <PrimaryTab to="/app/map" icon={<LayoutGrid className="h-4 w-4" />} label={t('app:nav.map')} />
          <PrimaryTab to="/app/explorer" icon={<Compass className="h-4 w-4" />} label={t('app:nav.explorer')} />
          {/* Dashboards: v2 — hidden for MVP */}
        </nav>
      )}

      <div className="flex-1" />

      {isAnalyst && (
        <Button asChild variant="secondary" size="sm" className="hidden lg:inline-flex">
          <Link to="/studio/morning">{t('studio:shell')}</Link>
        </Button>
      )}

      {isExternalClient && <QuotaIndicator />}

      {isInternalUser && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={t('common:userMenu.notifications')}>
              <Bell className="h-4 w-4" aria-hidden />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('common:userMenu.notifications')}</TooltipContent>
        </Tooltip>
      )}

      <LanguageSwitcher />

      <UserMenu showAdminLink={isAdmin} />
    </header>
  );
}

// ─── Primary nav tab ────────────────────────────────────────────

function PrimaryTab({
  to,
  icon,
  label,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-body font-medium transition-colors',
          isActive
            ? 'bg-brand-50 text-brand-900'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
        )
      }
    >
      {icon}
      {label}
    </NavLink>
  );
}

// ─── Quota indicator ───────────────────────────────────────────

function QuotaIndicator() {
  // Live remaining count is set on the QueryResponse — we read the last value
  // saved by ChatStore. Hooks live in chat module; for now show the tier badge.
  const { t } = useTranslation('common');
  const tier = useTier();
  if (!tier) return null;
  const variant = tier === 'free' ? 'warning' : 'accent';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <Badge variant={variant}>{tier}</Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent>{t('userMenu.tier', { tier })}</TooltipContent>
    </Tooltip>
  );
}

// ─── User menu ──────────────────────────────────────────────────

function UserMenu({ showAdminLink }: { showAdminLink: boolean }) {
  const { t } = useTranslation('common');
  const { user, logout } = useAuth();
  if (!user) return null;

  const initials = (user.name || user.email || '??')
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t('userMenu.profile')}>
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-50 text-h4 text-brand-900">
            {initials}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/app/portal/home" className="flex items-center gap-2">
            <UserIcon className="h-3.5 w-3.5" aria-hidden /> {t('userMenu.profile')}
          </Link>
        </DropdownMenuItem>
        {showAdminLink && (
          <DropdownMenuItem asChild>
            <Link to="/app/admin/users" className="flex items-center gap-2">
              <Settings className="h-3.5 w-3.5" aria-hidden /> {t('userMenu.admin')}
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void logout()} className="text-red-700">
          <LogOut className="mr-2 h-3.5 w-3.5" aria-hidden />
          {t('userMenu.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
